import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateHighlightPinPayload,
  UpsertHighlightOverridePayload,
} from '@/client/types/highlights';
import { highlightsKeys } from './highlights.query';

const HIGHLIGHTS = '/api/v2/admin/highlights';
const TIMEOUT = { timeout: 30_000 };

export const useCreateHighlightPinMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: CreateHighlightPinPayload) => {
    return request<any>(`${HIGHLIGHTS}/pin`, 'POST', payload, TIMEOUT);
  }, 'Failed to create pin');
  return useMutation({
    mutationFn: (payload: CreateHighlightPinPayload) => fn(payload),
    onSuccess: (_d, { etid }) => qc.invalidateQueries({ queryKey: highlightsKeys.pins(etid) }),
  });
};

export const useDeleteHighlightPinMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { hpid: number; etid: number }) => {
    return request<any>(`${HIGHLIGHTS}/pin/${args.hpid}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to delete pin');
  return useMutation({
    mutationFn: (args: { hpid: number; etid: number }) => fn(args),
    onSuccess: (_d, { etid }) => qc.invalidateQueries({ queryKey: highlightsKeys.pins(etid) }),
  });
};

export const useUpsertHighlightOverrideMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: UpsertHighlightOverridePayload) => {
    return request<any>(`${HIGHLIGHTS}/override`, 'PUT', payload, TIMEOUT);
  }, 'Failed to save override');
  return useMutation({
    mutationFn: (payload: UpsertHighlightOverridePayload) => fn(payload),
    onSuccess: (_d, { etid }) => qc.invalidateQueries({ queryKey: highlightsKeys.override(etid) }),
  });
};

export const useDeleteHighlightOverrideMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (etid: number) => {
    return request<any>(`${HIGHLIGHTS}/override/${etid}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to delete override');
  return useMutation({
    mutationFn: (etid: number) => fn(etid),
    onSuccess: (_d, etid) => qc.invalidateQueries({ queryKey: highlightsKeys.override(etid) }),
  });
};
